// Runtime frames for parse-time backreferences. Plan: docs/prev-obj-static-binding-plan.md §3.4.
//
// One frame per compilation unit in flight. It holds the slot table takeAction writes, plus the
// referents the binder names but no action produces: `self` (this card), `trigger` (what fired a
// reaction), `target` (the card whose operations we are copying) and `loop` (the current item of
// a "for each" body).
//
// Two rules make the frame stack safe to build before anything reads it:
//
//   * it is maintained unconditionally – pushing, popping and recording are pure bookkeeping,
//     so turning the pref on changes what is *consulted*, never what was *recorded*;
//   * it lives at state.frames, outside state.cache. The cache is wiped wholesale whenever a
//     card activates, which would silently drop an outer card's slots half way through its own
//     effect. A state snapshot/restore (undo, a cancelled action) still rolls frames back with
//     everything else, which is what we want.
//
// Slot ids are `<unit>:<n>` (see bind_prev.js nextSlot). The unit prefix is what lets a read
// tell "that action ran and produced nothing" (unit in flight, slot empty → []) from "that unit
// is not running, I have no idea" (→ null, caller falls back to the legacy scan).
import { state } from './stores.js';
import { get } from 'svelte/store';

let nextToken = 0;

export function unitOf(slot){
    return typeof slot == 'string' && slot.includes(':') ? slot.split(':')[0] : null;
}

// ---------------------------------------------------------------------------------------
// Pure core. Every function takes and returns plain values, so it is testable without a game.
// ---------------------------------------------------------------------------------------

export function newFrame({unit = null, card = null, trigger = null, target = null} = {}){
    return {token: ++nextToken, unit, self: card, trigger, target, loop: null, slots: {}};
}

// Push, and pop back to (and including) a token. Pop is deliberately forgiving: a cancelled
// action can restore a state snapshot taken before the push, so the frame may already be gone.
export function pushed(stack, frame){
    return [...(stack || []), frame];
}

export function popped(stack, token){
    if(!stack || !stack.length) return stack || [];
    let i = stack.findIndex(f => f.token == token);
    return i == -1 ? stack : stack.slice(0, i);
}

// The frame a slot belongs to: the innermost one stamped with that slot's unit. Falls back to
// the innermost frame for pseudo-referents ('self'/'trigger'/'loop'), which have no unit.
export function frameForSlot(stack, slot){
    if(!stack || !stack.length) return null;
    let unit = unitOf(slot);
    if(!unit) return stack[stack.length - 1];
    for(let i = stack.length - 1; i >= 0; i--){
        if(stack[i].unit == unit) return stack[i];
    }
    return null;
}

// takeAction calls this once per object of a multi-object action, so parts accumulate.
export function recordInto(frame, slot, {obj, to, from} = {}){
    if(!frame || slot === undefined || slot === null) return frame;
    let entry = frame.slots[slot] || {obj: [], to: [], from: []};
    if(obj !== undefined && obj !== null) entry.obj = [...entry.obj, ...arr(obj)];
    if(to) entry.to = [...entry.to, ...arr(to)];
    if(from) entry.from = [...entry.from, ...arr(from)];
    frame.slots[slot] = entry;
    return frame;
}

function arr(v){
    if(v === null || v === undefined) return [];
    return Array.isArray(v) ? v : [v];
}

// Nearest frame with a value for one of the named referents.
function nearest(stack, key){
    for(let i = (stack || []).length - 1; i >= 0; i--){
        let v = stack[i][key];
        if(v !== null && v !== undefined) return v;
    }
    return null;
}

// Resolve a compile-time binding against the stack.
//   array  – the answer, possibly empty ("that produced nothing")
//   null   – this stack cannot answer; the caller falls back to the legacy scan
export function readBinding(stack, bind){
    if(!bind) return null;
    let fromSlot = slot => {
        let frame = frameForSlot(stack, slot);
        if(!frame) return null; // unit not in flight
        let entry = frame.slots[slot];
        if(!entry){
            // An action that ran always records, so an empty slot means it produced nothing –
            // a real answer, and the whole point of binding. A `via` referent (a condition
            // subject, a goal element) is only recorded where a writer has been wired up, so
            // an empty one means "not implemented", and must fall back to the scan instead.
            return bind.via ? null : [];
        }
        let part = bind.part || 'obj';
        if(entry[part] && entry[part].length) return arr(entry[part]);
        // A test's location ("... is at a [[location]]") is recorded only where a writer exists;
        // without one, the tested card itself is not the answer, so decline to the scan.
        if(bind.via && part != 'obj') return null;
        return arr(entry.obj);
    };
    if(bind.kind == 'slot') return fromSlot(bind.slot);
    if(bind.kind == 'slots'){
        // Mutually exclusive branches: take whichever one actually ran.
        let any = false, out = [];
        for(let s of bind.slots || []){
            let got = fromSlot(s);
            if(got === null) continue;
            any = true;
            out.push(...got);
        }
        return any ? out : null;
    }
    if(bind.kind == 'self'){
        // Not inherited: every unit belongs to a card, so an unset `self` means we are outside
        // any frame (or in a stardate, which has no card). Decline rather than answer nothing -
        // the scan resolves "this card" from currentCard().
        let f = stack && stack.length ? stack[stack.length - 1] : null;
        return f && f.self !== null && f.self !== undefined ? [f.self] : null;
    }
    if(bind.kind == 'trigger'){
        // reactionObj entries are each a whole action's obj, often itself an array
        // ("log the top card" pushes [id]), so flatten to the cards themselves
        let t = nearest(stack, 'trigger');
        return t === null ? null : arr(t).flat(Infinity);
    }
    if(bind.kind == 'loop'){
        let l = nearest(stack, 'loop');
        return l === null ? null : arr(l);
    }
    if(bind.kind == 'target'){
        let t = nearest(stack, 'target');
        return t === null ? null : arr(t);
    }
    return null; // count, same, unbound
}

// "If you gained 3 or more": how many a slot produced, or null if it cannot be known.
export function readCount(stack, bind){
    if(!bind || bind.kind != 'count') return null;
    let frame = frameForSlot(stack, bind.slot);
    if(!frame) return null;
    let entry = frame.slots[bind.slot];
    return entry ? entry.obj.length : 0;
}

// ---------------------------------------------------------------------------------------
// Store-bound wrappers. These are the only functions the game calls.
// ---------------------------------------------------------------------------------------

export function currentFrames(){
    return get(state).frames || [];
}

// The unit to push a frame for when resolving `effects`, or null when the innermost frame
// already belongs to them (or they carry no slots). Lets every resolveEffects entry point -
// enlist, log and clean-up triggers, rule consequences, bot charts - bind "it" to its own card
// and its own slots, not to whatever frame happens to be on top, or a stale root frame.
export function unitNeedingFrame(effects){
    let unit = unitOfEffects(effects);
    if(!unit) return null;
    let stack = currentFrames();
    let top = stack[stack.length - 1];
    return top && top.unit == unit && !top.root ? null : unit;
}

// The frame an effect is resolving in (innermost one for its unit), to hand to another client
// along with the effect, so its "it" means there what it meant here. Null if none.
export function frameFor(effects){
    let unit = unitOfEffects(effects);
    let frame = unit ? frameForSlot(currentFrames(), unit + ':') : null;
    return frame ? JSON.parse(JSON.stringify(frame)) : null;
}

// Push a copy of a frame received from another client; returns the token for popFrame.
export function adoptFrame(frame){
    let copy = {...JSON.parse(JSON.stringify(frame)), token: ++nextToken, root: false};
    state.update(s => {
        s.frames = pushed(s.frames, copy);
        return s;
    });
    return copy.token;
}

export function pushFrame(opts){
    let frame = newFrame(opts);
    state.update(s => {
        s.frames = pushed(s.frames, frame);
        return s;
    });
    return frame.token;
}

export function popFrame(token){
    state.update(s => {
        s.frames = popped(s.frames, token);
        return s;
    });
}

// The unit a tree of effects belongs to, read off the first slot the binder stamped. Unbound
// trees (old saves, hand-written effects) have none, and simply get a frame with no unit – so
// nothing binds into them and every reference inside falls back to the scan.
export function unitOfEffects(effects, seen = new Set()){
    if(!effects || typeof effects != 'object' || seen.has(effects)) return null;
    seen.add(effects);
    if(Array.isArray(effects)){
        for(let e of effects){
            let u = unitOfEffects(e, seen);
            if(u) return u;
        }
        return null;
    }
    if(typeof effects.slot == 'string'){
        let u = unitOf(effects.slot);
        if(u) return u;
    }
    for(let k of Object.keys(effects)){
        let u = unitOfEffects(effects[k], seen);
        if(u) return u;
    }
    return null;
}

export function recordSlot(slot, parts){
    if(slot === undefined || slot === null) return;
    state.update(s => {
        let stack = s.frames || [];
        let frame = frameForSlot(stack, slot);
        if(!frame){
            // No frame for this unit – an effect resolved outside any push site. Record into a
            // root frame rather than losing it; reads still check the unit before answering.
            frame = newFrame({unit: unitOf(slot)});
            frame.root = true; // never popped, so never reused as a later run's own frame
            stack = pushed(stack, frame);
        }
        recordInto(frame, slot, parts);
        s.frames = stack;
        return s;
    });
}

// Forget what the given action slots recorded. A replayed effect ("repeat this") runs the same
// slots again; without this "it" would still read the first run's card (first-recorded wins).
export function clearSlotsIn(tree){
    let slots = [];
    let walk = (n, seen = new Set()) => {
        if(!n || typeof n != 'object' || seen.has(n)) return;
        seen.add(n);
        if(Array.isArray(n)){ n.forEach(x => walk(x, seen)); return; }
        if(typeof n.slot == 'string') slots.push(n.slot);
        Object.keys(n).forEach(k => k != 'bind' && walk(n[k], seen));
    };
    walk(tree);
    if(!slots.length) return;
    state.update(s => {
        for(let f of s.frames || []){
            for(let sl of slots) if(f.slots && f.slots[sl]) delete f.slots[sl];
        }
        return s;
    });
}

// Set/clear the current item of a "for each" body on the innermost frame. Returns the previous
// value so the caller can restore it (loops nest).
export function setLoopObj(value){
    let prev = null;
    state.update(s => {
        let stack = s.frames || [];
        if(!stack.length) return s;
        prev = stack[stack.length - 1].loop;
        stack[stack.length - 1].loop = value === undefined ? null : value;
        return s;
    });
    return prev;
}

export function setTrigger(value){
    state.update(s => {
        let stack = s.frames || [];
        if(stack.length) stack[stack.length - 1].trigger = value === undefined ? null : value;
        return s;
    });
}

export function resolveFrameBinding(bind){
    return readBinding(currentFrames(), bind);
}

export function resolveFrameCount(bind){
    return readCount(currentFrames(), bind);
}
